import { Menu, usePermissions } from 'react-admin';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StyleIcon from '@mui/icons-material/Style';
import ShieldIcon from '@mui/icons-material/Shield';
import PaymentIcon from '@mui/icons-material/Payment';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TuneIcon from '@mui/icons-material/Tune';
import CategoryIcon from '@mui/icons-material/Category';
import WorkIcon from '@mui/icons-material/Work';
import PublicIcon from '@mui/icons-material/Public';
import SecurityIcon from '@mui/icons-material/Security';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReportIcon from '@mui/icons-material/Report';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import StoreIcon from '@mui/icons-material/Store';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { AdminPermission } from '../types/admin.types';

/**
 * Check if user has permission
 */
function canAccess(
  permissions: AdminPermission[] | undefined,
  required: AdminPermission[]
): boolean {
  if (!permissions) return false;
  return required.some((p) => permissions.includes(p));
}

/**
 * Menu section label component
 */
function MenuSection({ label }: { label: string }) {
  return (
    <Box sx={{ px: 2, py: 1, mt: 1 }}>
      <Typography
        variant="overline"
        sx={{
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '0.65rem',
          fontWeight: 600,
          letterSpacing: '0.1em',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Admin sidebar menu
 * Shows/hides items based on user permissions
 */
export function AdminMenu() {
  const { permissions } = usePermissions<AdminPermission[]>();

  return (
    <Menu>
      {/* Dashboard - visible to all */}
      <Menu.DashboardItem primaryText="Tableau de bord" leftIcon={<DashboardIcon />} />

      <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* Users Section */}
      {canAccess(permissions, [AdminPermission.USERS_READ]) && (
        <>
          <MenuSection label="Utilisateurs" />
          <Menu.ResourceItem
            name="users"
            primaryText="Utilisateurs"
            leftIcon={<PeopleIcon />}
          />
        </>
      )}

      {/* Cards Section */}
      {canAccess(permissions, [AdminPermission.CARDS_READ]) && (
        <>
          <MenuSection label="Cartes Digitales" />
          <Menu.ResourceItem
            name="cards"
            primaryText="Toutes les cartes"
            leftIcon={<CreditCardIcon />}
          />
          <Menu.Item
            to="/templates"
            primaryText="Templates"
            leftIcon={<StyleIcon />}
          />
          {canAccess(permissions, [AdminPermission.CARDS_MODERATE]) && (
            <Menu.Item
              to="/moderation"
              primaryText="Modération"
              leftIcon={<ShieldIcon />}
            />
          )}
          {/* V1e · Modération credentials LIBERAL — réutilise CARDS_MODERATE
              car les credentials sont liés à un DigitalCard. Si on devait
              séparer les permissions plus tard, ajouter CREDENTIALS_MODERATE. */}
          {canAccess(permissions, [AdminPermission.CARDS_MODERATE]) && (
            <Menu.Item
              to="/credentials/moderation"
              primaryText="Credentials LIBERAL"
              leftIcon={<ShieldIcon />}
            />
          )}
        </>
      )}

      {/* Monetization Section */}
      {canAccess(permissions, [
        AdminPermission.PLANS_READ,
        AdminPermission.FINANCE_READ,
      ]) && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <MenuSection label="Monétisation" />

          {canAccess(permissions, [AdminPermission.PLANS_READ]) && (
            <>
              <Menu.ResourceItem
                name="plans"
                primaryText="Forfaits"
                leftIcon={<PaymentIcon />}
              />
              <Menu.ResourceItem
                name="subscriptions"
                primaryText="Abonnements"
                leftIcon={<SubscriptionsIcon />}
              />
              <Menu.ResourceItem
                name="currencies"
                primaryText="Devises"
                leftIcon={<CurrencyExchangeIcon />}
              />
            </>
          )}

          {canAccess(permissions, [AdminPermission.FINANCE_READ]) && (
            <>
              <Menu.Item
                to="/payments"
                primaryText="Dashboard Paiements"
                leftIcon={<AccountBalanceIcon />}
              />
              <Menu.Item
                to="/payments/transactions"
                primaryText="Transactions"
                leftIcon={<ReceiptLongIcon />}
              />
              <Menu.Item
                to="/payments/providers"
                primaryText="Comparaison Providers"
                leftIcon={<CompareArrowsIcon />}
              />
            </>
          )}

          {/* Payment Config - PAY-008 */}
          {canAccess(permissions, [AdminPermission.CONFIG_WRITE]) && (
            <>
              <Menu.Item
                to="/payments/hybrid-limits"
                primaryText="Limites Hybrides"
                leftIcon={<TuneIcon />}
              />
              <Menu.Item
                to="/payments/country-routing"
                primaryText="Routing Pays"
                leftIcon={<PublicIcon />}
              />
              <Menu.Item
                to="/payments/audit-logs"
                primaryText="Audit Config"
                leftIcon={<SecurityIcon />}
              />
            </>
          )}
        </>
      )}

      {/* Terrain Section — visible aux ADMIN */}
      {canAccess(permissions, [AdminPermission.USERS_READ]) && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <MenuSection label="Terrain" />
          <Menu.Item
            to="/terrain"
            primaryText="MID Supervisors"
            leftIcon={<SupervisorAccountIcon />}
          />
        </>
      )}

      {/* Partners Section */}
      {canAccess(permissions, [AdminPermission.PARTNERS_READ]) && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <MenuSection label="Partners" />
          <Menu.Item
            to="/partners"
            primaryText="Gestion Partners"
            leftIcon={<StoreIcon />}
          />
        </>
      )}

      {/* Engagement Section */}
      {canAccess(permissions, [
        AdminPermission.REWARDS_READ,
        AdminPermission.ACADEMY_READ,
      ]) && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <MenuSection label="Engagement" />

          {canAccess(permissions, [AdminPermission.REWARDS_READ]) && (
            <Menu.Item
              to="/rewards"
              primaryText="Rewards"
              leftIcon={<EmojiEventsIcon />}
            />
          )}

          {canAccess(permissions, [AdminPermission.ACADEMY_READ]) && (
            <Menu.Item
              to="/academy"
              primaryText="Academy"
              leftIcon={<SchoolIcon />}
            />
          )}
        </>
      )}

      {/* Operations Section */}
      {canAccess(permissions, [
        AdminPermission.NOTIFICATIONS_READ,
        AdminPermission.SUPPORT_READ,
        AdminPermission.MODERATION_READ,
      ]) && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <MenuSection label="Opérations" />

          {canAccess(permissions, [AdminPermission.NOTIFICATIONS_READ]) && (
            <Menu.Item
              to="/notifications"
              primaryText="Notifications"
              leftIcon={<NotificationsIcon />}
            />
          )}

          {canAccess(permissions, [AdminPermission.SUPPORT_READ]) && (
            <Menu.Item
              to="/support"
              primaryText="Support"
              leftIcon={<SupportAgentIcon />}
            />
          )}

          {canAccess(permissions, [AdminPermission.MODERATION_READ]) && (
            <Menu.Item
              to="/reports"
              primaryText="Signalements"
              leftIcon={<ReportIcon />}
            />
          )}
        </>
      )}

      {/* Guard Services Section */}
      {canAccess(permissions, [AdminPermission.GUARD_READ]) && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <MenuSection label="Services locaux" />
          <Menu.Item
            to="/guard"
            primaryText="Tableau de bord Garde"
            leftIcon={<span style={{ fontSize: 18 }}>🏥</span>}
          />
          <Menu.Item
            to="/guard-zones"
            primaryText="Zones de garde"
            leftIcon={<PublicIcon />}
          />
          {canAccess(permissions, [AdminPermission.GUARD_IMPORT]) && (
            <Menu.Item
              to="/guard-import"
              primaryText="Import planning"
              leftIcon={<span style={{ fontSize: 18 }}>📥</span>}
            />
          )}
        </>
      )}

      {/* Analytics & Config Section */}
      {canAccess(permissions, [
        AdminPermission.ANALYTICS_READ,
        AdminPermission.CONFIG_READ,
        AdminPermission.AUDIT_READ,
      ]) && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <MenuSection label="Système" />

          {canAccess(permissions, [AdminPermission.ANALYTICS_READ]) && (
            <Menu.Item
              to="/analytics"
              primaryText="Analytics"
              leftIcon={<AnalyticsIcon />}
            />
          )}

          {canAccess(permissions, [AdminPermission.CONFIG_READ]) && (
            <Menu.Item
              to="/settings"
              primaryText="Paramètres"
              leftIcon={<SettingsIcon />}
            />
          )}

          {canAccess(permissions, [AdminPermission.CONFIG_READ]) && (
            <Menu.Item
              to="/sectors"
              primaryText="Secteurs d'activité"
              leftIcon={<CategoryIcon />}
            />
          )}

          {canAccess(permissions, [AdminPermission.CONFIG_READ]) && (
            <Menu.Item
              to="/sector-specialties"
              primaryText="Spécialités"
              leftIcon={<WorkIcon />}
            />
          )}

          {canAccess(permissions, [AdminPermission.AUDIT_READ]) && (
            <Menu.Item
              to="/audit"
              primaryText="Logs d'audit"
              leftIcon={<HistoryIcon />}
            />
          )}
        </>
      )}
    </Menu>
  );
}

export default AdminMenu;
